import { z } from 'zod';
import { NodeTypeSchema, NodeStatusSchema } from './node-types.schema';
import { AiModelPurposeSchema } from '../../ai/model.schema';
import { RuntimeModeSchema, ExtractedParamSchema, ShaderActiveTabSchema } from '../sketch/sketch.schema';

/**
 * Node State Schemas
 * Defines state structures for node execution and persistence
 */

// Created item (AI generation result)
export const CreatedItemSchema = z.object({
  id: z.string(),
  type: NodeTypeSchema,
  content: z.union([z.string(), z.instanceof(File), z.instanceof(Blob)]),
  metadata: z.object({
    name: z.string().optional(),
    jobId: z.string().optional(),
    studioInputs: z.array(z.object({ frameId: z.string(), versionId: z.string() })).optional(),
    size: z.number().optional(),
    duration: z.number().optional(),
    dimensions: z.object({
      width: z.number(),
      height: z.number()
    }).optional(),
    createdAt: z.date(),
    prompt: z.string().optional(),
    settings: z.record(z.string(), z.unknown()).optional(),

    // Parent-child relationship (for generated items)
    isGenerated: z.boolean().optional().default(true),     // Distinguishes generated vs manual
    generatorNodeId: z.string().optional(),                 // Parent node that generated this item

    // ✅ Generated parameters (used to create this item - for child node settings)
    generatedParams: z.object({
      model: z.string(),
      parameters: z.record(z.string(), z.unknown()),
    }).optional(),

    // File reference (for uploaded/generated files)
    fileUuid: z.string().optional(),

    // Multi-resolution versions (for images)
    versions: z.object({
      original: z.object({
        url: z.string(),
        width: z.number().optional(),
        height: z.number().optional(),
      }).optional(),
      lowRes: z.object({
        url: z.string(),
        width: z.number().optional(),
        height: z.number().optional(),
      }).optional(),
      highRes: z.object({
        url: z.string(),
        width: z.number().optional(),
        height: z.number().optional(),
      }).optional(),
    }).optional(),

    // ✅ SNAPSHOT: Complete state at generation time (allows restoring parent settings when clicking generated items)
    generationSnapshot: z.object({
      modelId: z.string(),              // Model used for generation
      brandFamily: z.string().optional(), // Brand family (legacy, backward compat)
      modelFamilySlug: z.string().optional(), // Model family slug (v2)
      settings: z.record(z.string(), z.unknown()), // All parameter values
      prompt: z.string(),               // Prompt used
      systemPrompt: z.string().optional(), // System prompt (text nodes only)
    }).optional(),
  }).optional(),
});

// Programmatic action (for external control - transient, not persisted long-term)
export const NodeActionSchema = z.object({
  type: z.enum(['generate', 'cancel', 'clear', 'update']),
  ts: z.number(), // Timestamp to ensure uniqueness
  params: z.any().optional(), // Action-specific parameters
});

// Orchestrator state (execution state - NOT persisted)
export const NodeOrchestratorStateSchema = z.object({
  status: NodeStatusSchema,
  message: z.string().optional(),
  progress: z.number().min(0).max(100).optional(),
  startedAt: z.date().optional(),
});

// Genie node state (persisted to Magic Canvas)
export const GenieNodeStateSchema = z.object({
  // Node identity
  title: z.string().optional(),
  // Absent on older documents: preserve an existing title rather than infer user intent from its text.
  titleSource: z.enum(['automatic', 'custom']).optional(),
  // Absent on older documents: a saved width+height pair is kept. `manual` means a corner drag that content must not replace.
  sizeSource: z.enum(['default', 'auto', 'manual']).optional(),
  modelName: z.string().optional(), // Display label for the saved modelId, available while the catalog loads
  purpose: AiModelPurposeSchema.optional().default('create'), // Node purpose (affects model filtering)
  subPurpose: z.string().optional(), // SubPurpose (specific capability within purpose)

  // Generation inputs
  prompt: z.string().optional(),
  context: z.string().optional(),

  // Media inputs
  image: z.union([z.string(), z.instanceof(File)]).optional(),
  images: z.array(z.union([z.string(), z.instanceof(File)])).optional(),
  video: z.union([z.string(), z.instanceof(File)]).optional(),

  // Generation results
  createdItems: z.array(CreatedItemSchema).optional(),
  generationError: z.string().optional(),

  // Canvas managed states
  selected: z.boolean().optional(),
  collapsed: z.boolean().optional(),
  locked: z.boolean().optional(), // Node locked during generation

  // Parent-child relationship (synced with Magic Canvas NodeData.parentId)
  parentNodeId: z.string().optional(),  // Parent node ID (if this is a child node)

  // Settings (for future settings system)
  quantity: z.number().optional(),
  mode: z.string().optional(),
  style: z.string().optional(),
  isStyleLinked: z.boolean().optional(),

  // Node-specific configuration (flexible JSON for custom nodes)
  nodeConfig: z.record(z.string(), z.unknown()).optional(),

  // Shader node specific fields
  runtimeMode: RuntimeModeSchema.optional(),
  sketchId: z.string().optional(),
  lastPrompt: z.string().optional(),
  selectedLlm: z.string().optional(),
  librarySketchId: z.string().optional(),
  forkedFrom: z.string().optional(),
  previewFps: z.number().optional().default(60),
  previewPaused: z.boolean().optional(),
  extractedParams: z.array(ExtractedParamSchema).optional(),
  paramValues: z.record(z.string(), z.unknown()).optional(),
  activeTab: ShaderActiveTabSchema.optional(),

  // ✅ SNAPSHOT: Generation state snapshot (for TextFile/ImageFile nodes created from generated items)
  generationSnapshot: z.object({
    modelId: z.string(),
    brandFamily: z.string().optional(), // Legacy, backward compat
    modelFamilySlug: z.string().optional(), // Model family slug (v2)
    settings: z.record(z.string(), z.unknown()),
    prompt: z.string(),
    systemPrompt: z.string().optional(),
  }).optional(),

  // Custom Nodes support
  pinnedParameters: z.array(z.string()).optional(),  // Parameter names pinned to node surface
  customNodeId: z.string().optional(),               // Reference to CustomNode definition (Phase 2)

  // Programmatic action trigger (transient)
  _action: NodeActionSchema.nullable().optional(),
}).passthrough(); // Allow additional fields for extensibility

// TypeScript types
export type CreatedItem = z.infer<typeof CreatedItemSchema>;
export type NodeAction = z.infer<typeof NodeActionSchema>;
export type NodeOrchestratorState = z.infer<typeof NodeOrchestratorStateSchema>;
export type GenieNodeState = z.infer<typeof GenieNodeStateSchema>;
